/**
 * ipc.ts — IPC Renderer API Bindings (Renderer Side)
 *
 * Типизированная обёртка над window.smeet для использования в React.
 * Все вызовы безопасны — возвращают undefined если не в Electron.
 */

import { isDesktop } from './platform';

const api = () => (isDesktop ? window.smeet! : null);

/** Dock-бейдж с числом непрочитанных */
export function setDockBadge(count: number): void {
  api()?.badge.set(count);
}

/** Нативное macOS уведомление */
export async function showNativeNotification(options: {
  title: string;
  body: string;
  chatId?: string;
  imageUrl?: string;
}): Promise<void> {
  await api()?.notifications.show(options);
}

/** Запрос разрешения на уведомления */
export async function requestNotificationPermission(): Promise<'granted' | 'denied' | 'unknown'> {
  const result = await api()?.notifications.requestPermission();
  return result ?? 'unknown';
}

/** Получить список источников для демонстрации экрана */
export async function getScreenCaptureSources() {
  return api()?.screenCapture.getSources() ?? [];
}

/** Безопасное хранилище (macOS Keychain) */
export const keychain = {
  get: (key: string) => api()?.keychain.get(key) ?? Promise.resolve(null),
  set: (key: string, value: string) => api()?.keychain.set(key, value) ?? Promise.resolve(),
  delete: (key: string) => api()?.keychain.delete(key) ?? Promise.resolve(),
};

/** Обновление приложения */
export const updater = {
  check: () => api()?.updater.checkForUpdates(),
  install: () => api()?.updater.installUpdate(),
  onAvailable: (cb: (info: { version: string }) => void) =>
    api()?.updater.onUpdateAvailable(cb) ?? (() => {}),
  onDownloaded: (cb: (info: { version: string }) => void) =>
    api()?.updater.onUpdateDownloaded(cb) ?? (() => {}),
  onProgress: (cb: (p: { percent: number }) => void) =>
    api()?.updater.onUpdateProgress(cb) ?? (() => {}),
};

/** Deep link подписка */
export function onDeepLink(callback: (url: string) => void): () => void {
  return api()?.onDeepLink(callback) ?? (() => {});
}

/** Управление окном */
export const windowControls = {
  minimize: () => api()?.window.minimize(),
  maximize: () => api()?.window.maximize(),
  close: () => api()?.window.close(),
  hide: () => api()?.window.hide(),
  show: () => api()?.window.show(),
};

/**
 * Нативный UI входящего звонка (macOS): всплывающее окно + рингтон + dock-bounce
 * + системный Notification с кнопками Ответить/Отклонить.
 *
 * Вызывается renderer'ом из IncomingCallModal когда GraphQL-подписка
 * `subscribeIncomingCall` получила событие И окно НЕ в фокусе — тогда
 * стандартная React-модалка остаётся скрытой, а нативный UI гарантирует,
 * что пользователь увидит звонок даже если работает в другом Space'е.
 */
export const incomingCall = {
  show: (payload: { callId: string; callerName: string; callerAvatarUrl?: string; isVideo: boolean }) =>
    api()?.incomingCall.show(payload),
  dismiss: () => api()?.incomingCall.dismiss(),
  onAnswered: (cb: (payload: { callId: string }) => void) =>
    api()?.incomingCall.onAnswered(cb) ?? (() => {}),
  onDeclined: (cb: (payload: { callId: string }) => void) =>
    api()?.incomingCall.onDeclined(cb) ?? (() => {}),
};

/** Session cookie sync (Electron) */
export const sessionCookie = {
  set: (token: string) => api()?.session.setCookie(token) ?? Promise.resolve(),
  clear: () => api()?.session.clearCookie() ?? Promise.resolve(),
};
